// #6788: remembering, across one reload, that the reload was the purchase-first checkout's.
//
// The two iPhone buyers in #6788 got a silent local store on the start right after that
// reload. The reports filed on that start say whether it followed the purchase reload, so
// the next reading can tell this group apart from the older silent stores on Windows.
//
// sessionStorage, because it survives `location.reload()` and is gone when the tab closes:
// a flag that outlived the tab would label some unrelated later start. Read once and
// removed at once, for the same reason.
//
// Injectable storage, no Angular imports: the unit test drives it with a plain object.

/** The subset of sessionStorage this needs. */
export interface AfterPurchaseReloadStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const kAfterPurchaseReloadKey = 'cs_after_purchase_reload';

/** Note that the reload about to happen is the purchase-first checkout's. Never throws. */
export function markAfterPurchaseReload(storage: AfterPurchaseReloadStorage | undefined | null): void {
  try {
    storage?.setItem(kAfterPurchaseReloadKey, '1');
  } catch {
    // not reported: the flag only labels later reports, and a storage that refuses it
    // leaves them saying `false`, which is what they said before this existed
  }
}

/** How the wait before the purchase reload ended (#6788). */
export enum UserDbInitWaitEnding {
  /** The user database's set-up finished, or failed: either way the store is no longer mid-open. */
  Settled = 'Settled',
  /** The cap ran out first, so the reload goes ahead exactly as it did before #6788. */
  CapReached = 'CapReached',
}

/** Everything `waitForUserDbInitAsync` needs, injected so a test can drive it without timers. */
export interface UserDbInitWaitDeps {
  /** The set-up running right now, or null when none has started yet. Never rejects. */
  inFlight: () => Promise<void> | null;
  /** Wait this many milliseconds. */
  sleepAsync: (ms: number) => Promise<void>;
  /** The time now, in milliseconds. */
  nowMs: () => number;
}

/** How often to look again for a set-up that has not started yet. */
const kUserDbInitPollMs = 100;

/**
 * Wait, at most `maxMs`, for the user database's set-up to finish (#6788).
 *
 * Signing in starts the set-up on the subscribe page, and the purchase-first checkout used to
 * reload straight away. The suspected cause of the two silent stores in #6788 is that reload
 * cutting off the brand-new store's first open. Waiting lets that open finish first. On a
 * healthy phone it takes well under a second; the cap means a slow phone reloads as before.
 *
 * The set-up may not have started yet when this is called, so it looks again every
 * `kUserDbInitPollMs` until it has, or until the cap runs out.
 */
export async function waitForUserDbInitAsync(maxMs: number, deps: UserDbInitWaitDeps): Promise<UserDbInitWaitEnding> {
  const deadline = deps.nowMs() + maxMs;
  // while no set-up has started, look again until one does or the cap runs out
  let inFlight = deps.inFlight();
  while (inFlight === null) {
    const remaining = deadline - deps.nowMs();
    if (remaining <= 0) return UserDbInitWaitEnding.CapReached;
    await deps.sleepAsync(Math.min(kUserDbInitPollMs, remaining));
    inFlight = deps.inFlight();
  }
  const remaining = deadline - deps.nowMs();
  if (remaining <= 0) return UserDbInitWaitEnding.CapReached;
  return Promise.race([
    inFlight.then(() => UserDbInitWaitEnding.Settled),
    deps.sleepAsync(remaining).then(() => UserDbInitWaitEnding.CapReached),
  ]);
}

/** Read the note once and remove it: true when this page load followed the purchase reload. */
export function takeAfterPurchaseReload(storage: AfterPurchaseReloadStorage | undefined | null): boolean {
  if (!storage) return false;
  try {
    const present = storage.getItem(kAfterPurchaseReloadKey) != null;
    if (present) storage.removeItem(kAfterPurchaseReloadKey);
    return present;
  } catch {
    // same reason as above: the answer only labels reports
    return false;
  }
}

/** This page load's answer, once read. Module state on purpose: the note is removed when it
 *  is read, so every reporter on the page must share the one reading. */
let pageAnswer: boolean | undefined;

/** True when this page load followed the purchase-first checkout's reload (#6788). */
export function pageStartedAfterPurchaseReload(): boolean {
  if (pageAnswer === undefined) {
    pageAnswer = takeAfterPurchaseReload(typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  }
  return pageAnswer;
}
